import {
  CA_CERTIFICATE_FIELDS,
  INVALID_CA_CERTIFICATE_MESSAGE,
} from "../../../shared/connectors/caCertificate";
import { validCaCertificateUpload } from "./validateCaCertificate";
import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useRef, useState } from "react";
import type { FileField } from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { trySealSecret } from "../../sealSecret";
import { emitErrorToast } from "../../lib/errorToast";
import { Box } from "../Box";
import { Icon } from "../Icon";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import type { App } from "./Form";

const MAX_FILE_BYTES = 1024 * 1024;

const UploadButton = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: var(--surface-primary);
  color: var(--text-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  border: 0;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  height: 32px;
  cursor: pointer;
  transition: box-shadow 100ms ease-out;
  &:hover {
    background: var(--alpha4);
  }
`;

const SavedRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
`;

type Props = {
  item: FileField;
  app: App;
  disabled?: boolean;
};

export function FileFieldItem({ item, app, disabled }: Props) {
  const z = useZero();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const { target } = item;

  // `<input type="file">` renders empty after save, so presence companions
  // let the UI show an existing secret without replicating its value.
  const hasSaved =
    target.kind === "configuration"
      ? (app.configuration?.[target.field]?.length ?? 0) > 0
      : target.kind === "secrets"
        ? (app.secretKeys?.includes(target.field) ?? false)
        : target.kind === "oauthConnection" && target.field === "clientSecret"
          ? (app.hasOAuthClientSecret ?? false)
          : false;

  const handleFile = (file: File) => {
    if (file.size > MAX_FILE_BYTES) {
      emitErrorToast({
        icon: "upload",
        message: `${file.name} is too large. ${item.label} must be under ${MAX_FILE_BYTES / (1024 * 1024)} MB.`,
        buttonLabel: "Dismiss",
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const text = reader.result as string;
      if (
        target.kind === "configuration" &&
        CA_CERTIFICATE_FIELDS.includes(target.field) &&
        !(await validCaCertificateUpload(text))
      ) {
        emitErrorToast({
          icon: "upload",
          message: INVALID_CA_CERTIFICATE_MESSAGE,
          buttonLabel: "Dismiss",
        });
        return;
      }
      const sealed =
        target.kind === "secrets" ? await trySealSecret(text) : undefined;
      if (target.kind === "secrets" && sealed === undefined) return;
      setFileName(file.name);

      const base = {
        connectedAppId: app.id,
        name: app.name,
        identifier: app.identifier,
        urlPattern: app.urlPattern,
        configuration: app.configuration,
      };

      if (target.kind === "secrets" && sealed !== undefined) {
        z.mutate(
          sharedMutators.updateConnectedApp({
            ...base,
            secrets: { [target.field]: sealed },
          })
        );
      } else if (target.kind === "configuration") {
        z.mutate(
          sharedMutators.updateConnectedApp({
            ...base,
            configuration: { ...app.configuration, [target.field]: text },
          })
        );
      } else if (
        target.kind === "oauthConnection" &&
        target.field !== "scopes" &&
        app.oauthConnectionId
      ) {
        z.mutate(
          sharedMutators.updateOAuthConnection({
            oauthConnectionId: app.oauthConnectionId,
            [target.field]: text,
          })
        );
      }
    };
    reader.readAsText(file);
  };

  const buttonLabel = fileName
    ? fileName
    : hasSaved
      ? "Replace file…"
      : "Choose file…";

  return (
    <div>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md" color="var(--text-primary)">
          {item.label}
        </Text>
      </Box>
      <input
        ref={inputRef}
        type="file"
        accept={item.accept}
        style={{ display: "none" }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) handleFile(file);
        }}
      />
      <SavedRow>
        <UploadButton
          onPress={() => inputRef.current?.click()}
          disabled={disabled}
        >
          <Icon name="upload" size={12} />
          <Text variant="text-small">{buttonLabel}</Text>
        </UploadButton>
        {hasSaved && !fileName && (
          <Box horizontal={4}>
            <Text variant="text-small" color="var(--text-secondary)">
              On file
            </Text>
          </Box>
        )}
      </SavedRow>
      {item.helperText && (
        <Box horizontal={8} top={6}>
          <Text variant="text-small" color="var(--text-secondary)">
            {item.helperText}
          </Text>
        </Box>
      )}
    </div>
  );
}
